import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2, Phone, Wallet, CreditCard, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BaseModulesSubscriptionDialog = ({ open, onSubscriptionSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [step, setStep] = useState('info'); // 'info', 'payment_method', 'orange_money', 'cash', 'cheque', 'processing'
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [invoiceId, setInvoiceId] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [ussdCode, setUssdCode] = useState('');
  const [polling, setPolling] = useState(false);
  
  // Cash payment fields
  const [cashPaymentDate, setCashPaymentDate] = useState('');
  const [cashReceiptRef, setCashReceiptRef] = useState('');
  const [cashNotes, setCashNotes] = useState('');
  
  // Cheque payment fields
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [chequeNotes, setChequeNotes] = useState('');

  useEffect(() => {
    if (open) {
      checkSubscriptionStatus();
    }
  }, [open]);

  const checkSubscriptionStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tenant/check-base-modules-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.has_subscribed) {
        // Déjà souscrit
        onSubscriptionSuccess();
      } else if (response.data.has_pending_payment && response.data.pending_payment) {
        // Il y a un paiement en attente, afficher le dialog de traitement
        const payment = response.data.pending_payment;
        setInvoiceId(payment.invoice_id);
        setPaymentId(payment.payment_id);
        setPaymentMethod(payment.payment_method);
        
        // Utiliser les données de souscription du backend
        setSubscriptionData({
          total_cost: response.data.total_cost || payment.amount,
          required_modules: response.data.required_modules || []
        });
        
        setStep('processing');
        
        // Reprendre le polling selon le type de paiement
        if (payment.payment_method === 'ORANGE_MONEY' && payment.transaction_id) {
          setTransactionId(payment.transaction_id);
          startPolling(payment.transaction_id);
        } else if (payment.payment_method === 'CASH' || payment.payment_method === 'CHEQUE') {
          startManualPaymentPolling(payment.payment_id);
        }
      } else {
        // Pas encore souscrit, afficher les infos
        setSubscriptionData(response.data);
        setStep('info');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      toast.error('Erreur lors de la vérification de la souscription');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateSubscription = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/tenant/subscribe-base-modules`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setInvoiceId(response.data.invoice_id);
      setStep('payment_method');
      toast.success('Facture créée. Choisissez votre méthode de paiement.');
    } catch (error) {
      console.error('Error initiating subscription:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la facture');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePayment = async () => {
    if (!phoneNumber.startsWith('+224')) {
      toast.error('Format invalide. Utilisez +224XXXXXXXXX');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/tenant/pay-base-modules/${invoiceId}?customer_phone=${encodeURIComponent(phoneNumber)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTransactionId(response.data.transaction_id);
      setUssdCode(response.data.ussd_code);
      setStep('processing');
      toast.success('Paiement initié! Composez le code USSD.');
      
      // Commencer le polling du statut
      startPolling(response.data.transaction_id);
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (txId) => {
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/tenant/base-modules-payment-status/${txId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status === 'SUCCESS') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.success('Paiement réussi! Accès activé.');
          setTimeout(() => {
            onSubscriptionSuccess();
          }, 2000);
        } else if (response.data.status === 'FAILED' || response.data.status === 'EXPIRED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.error('Le paiement a échoué. Veuillez réessayer.');
          setStep('payment');
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    }, 3000); // Poll toutes les 3 secondes

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setPolling(false);
    }, 300000);
  };


  const handleCashPayment = async () => {
    if (!cashPaymentDate || !cashReceiptRef) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/tenant/pay-base-modules-cash/${invoiceId}`,
        {
          amount: subscriptionData.total_cost,
          payment_date: cashPaymentDate,
          receipt_reference: cashReceiptRef,
          notes: cashNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentId(response.data.payment_id);
      setStep('processing');
      toast.success('Paiement enregistré! En attente de validation.');
      
      // Commencer le polling du statut
      startManualPaymentPolling(response.data.payment_id);
    } catch (error) {
      console.error('Error submitting cash payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleChequePayment = async () => {
    if (!chequeNumber || !bankName || !chequeDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/tenant/pay-base-modules-cheque/${invoiceId}`,
        {
          amount: subscriptionData.total_cost,
          cheque_number: chequeNumber,
          bank_name: bankName,
          cheque_date: chequeDate,
          notes: chequeNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentId(response.data.payment_id);
      setStep('processing');
      toast.success('Paiement enregistré! En attente de validation.');
      
      // Commencer le polling du statut
      startManualPaymentPolling(response.data.payment_id);
    } catch (error) {
      console.error('Error submitting cheque payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setLoading(false);
    }
  };

  const startManualPaymentPolling = (pId) => {
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/tenant/payment-status/${pId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status === 'VALIDATED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.success('Paiement validé! Accès activé.');
          setTimeout(() => {
            onSubscriptionSuccess();
          }, 2000);
        } else if (response.data.status === 'REJECTED') {
          clearInterval(pollInterval);
          setPolling(false);
          toast.error(`Paiement rejeté: ${response.data.rejection_reason}`);
          setStep('payment_method');
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    }, 5000); // Poll toutes les 5 secondes

    // Arrêter le polling après 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setPolling(false);
    }, 600000);
  };


  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-GN').format(price);
  };

  if (loading && !subscriptionData) {
    return (
      <Dialog open={open}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Chargement...</DialogTitle>
            <DialogDescription>Vérification de votre souscription en cours</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl">
            <AlertCircle className="h-6 w-6 mr-2 text-orange-600" />
            Souscription Obligatoire aux Modules de Base
          </DialogTitle>
          <DialogDescription>
            Pour accéder au système, vous devez souscrire aux modules de base obligatoires.
          </DialogDescription>
        </DialogHeader>

        {step === 'info' && subscriptionData && (
          <div className="space-y-6 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">
                <strong>Important:</strong> Ces modules sont essentiels au fonctionnement du système et doivent être activés avant toute utilisation.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Modules Requis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {subscriptionData.required_modules.map((module) => (
                  <Card key={module.module_id} className="border-2 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="text-center space-y-2">
                        <div className="text-3xl">{module.icon}</div>
                        <h4 className="font-bold text-sm">{module.name}</h4>
                        <p className="text-xs text-gray-600">{module.description}</p>
                        <div className="text-xl font-bold text-blue-600">
                          {formatPrice(module.monthly_price)} GNF
                        </div>
                        <p className="text-xs text-gray-500">par mois</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">Coût Total Mensuel</p>
                  <p className="text-sm text-gray-600">Paiement unique pour activer l'accès</p>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {formatPrice(subscriptionData.total_cost)} GNF
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                onClick={handleInitiateSubscription}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  'Procéder au Paiement'
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'payment_method' && (
          <div className="space-y-6 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">Montant à payer</p>
                  <p className="text-sm text-gray-600">Choisissez votre méthode de paiement</p>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {formatPrice(subscriptionData?.total_cost || 0)} GNF
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Méthodes de Paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Orange Money */}
                <Card 
                  className="border-2 hover:border-orange-500 cursor-pointer transition-colors"
                  onClick={() => {
                    setPaymentMethod('ORANGE_MONEY');
                    setStep('orange_money');
                  }}
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <Phone className="h-12 w-12 mx-auto text-orange-600" />
                      <h4 className="font-bold">Orange Money</h4>
                      <p className="text-xs text-gray-600">Paiement instantané via mobile</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Cash */}
                <Card 
                  className="border-2 hover:border-green-500 cursor-pointer transition-colors"
                  onClick={() => {
                    setPaymentMethod('CASH');
                    setStep('cash');
                  }}
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <Wallet className="h-12 w-12 mx-auto text-green-600" />
                      <h4 className="font-bold">Cash</h4>
                      <p className="text-xs text-gray-600">Paiement en espèces</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Chèque */}
                <Card 
                  className="border-2 hover:border-blue-500 cursor-pointer transition-colors"
                  onClick={() => {
                    setPaymentMethod('CHEQUE');
                    setStep('cheque');
                  }}
                >
                  <CardContent className="pt-6 pb-6">
                    <div className="text-center space-y-3">
                      <CreditCard className="h-12 w-12 mx-auto text-blue-600" />
                      <h4 className="font-bold">Chèque</h4>
                      <p className="text-xs text-gray-600">Paiement par chèque bancaire</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('info')}
              >
                Retour
              </Button>
            </div>
          </div>
        )}

        {step === 'orange_money' && (
          <div className="space-y-6 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">via Orange Money</p>
                </div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatPrice(subscriptionData?.total_cost || 0)} GNF
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Numéro Orange Money</Label>
              <div className="flex space-x-2 mt-2">
                <div className="flex-1">
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+224 6XX XX XX XX"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Format: +224 6XX XX XX XX (ex: +224 621 23 45 67)
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleInitiatePayment}
                disabled={loading || !phoneNumber}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-2" />
                    Payer via Orange Money
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'cash' && (
          <div className="space-y-6 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">Paiement Cash</p>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {formatPrice(subscriptionData?.total_cost || 0)} GNF
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Votre paiement sera vérifié par le Super Admin avant l'activation de votre accès.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="cash_date">Date de Paiement *</Label>
                <Input
                  id="cash_date"
                  type="date"
                  value={cashPaymentDate}
                  onChange={(e) => setCashPaymentDate(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cash_receipt">Référence du Reçu *</Label>
                <Input
                  id="cash_receipt"
                  type="text"
                  placeholder="Ex: REC-2024-001"
                  value={cashReceiptRef}
                  onChange={(e) => setCashReceiptRef(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Numéro du reçu de paiement délivré
                </p>
              </div>

              <div>
                <Label htmlFor="cash_notes">Notes (optionnel)</Label>
                <Input
                  id="cash_notes"
                  type="text"
                  placeholder="Informations supplémentaires..."
                  value={cashNotes}
                  onChange={(e) => setCashNotes(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleCashPayment}
                disabled={loading || !cashPaymentDate || !cashReceiptRef}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Wallet className="h-4 w-4 mr-2" />
                    Enregistrer le Paiement
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'cheque' && (
          <div className="space-y-6 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Montant à payer</p>
                  <p className="text-sm text-gray-600">Paiement par Chèque</p>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatPrice(subscriptionData?.total_cost || 0)} GNF
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Votre paiement sera vérifié par le Super Admin avant l'activation de votre accès.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="cheque_number">Numéro du Chèque *</Label>
                <Input
                  id="cheque_number"
                  type="text"
                  placeholder="Ex: 1234567"
                  value={chequeNumber}
                  onChange={(e) => setChequeNumber(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="bank_name">Nom de la Banque *</Label>
                <Input
                  id="bank_name"
                  type="text"
                  placeholder="Ex: BCRG, Ecobank, etc."
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cheque_date">Date du Chèque *</Label>
                <Input
                  id="cheque_date"
                  type="date"
                  value={chequeDate}
                  onChange={(e) => setChequeDate(e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="cheque_notes">Notes (optionnel)</Label>
                <Input
                  id="cheque_notes"
                  type="text"
                  placeholder="Informations supplémentaires..."
                  value={chequeNotes}
                  onChange={(e) => setChequeNotes(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('payment_method')}
                disabled={loading}
              >
                Retour
              </Button>
              <Button
                onClick={handleChequePayment}
                disabled={loading || !chequeNumber || !bankName || !chequeDate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Enregistrer le Paiement
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-4">
              {polling ? (
                <>
                  <Loader2 className="h-16 w-16 mx-auto animate-spin text-blue-600" />
                  <h3 className="text-xl font-semibold">
                    {paymentMethod === 'ORANGE_MONEY' ? 'Paiement en cours...' : 'En attente de validation...'}
                  </h3>
                  <p className="text-gray-600">
                    {paymentMethod === 'ORANGE_MONEY' 
                      ? 'Veuillez composer le code USSD sur votre téléphone pour confirmer le paiement.'
                      : 'Votre paiement est en cours de vérification par le Super Admin. Vous serez notifié une fois validé.'}
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
                  <h3 className="text-xl font-semibold text-green-600">Paiement réussi!</h3>
                  <p className="text-gray-600">
                    Votre accès aux modules de base est maintenant activé.
                  </p>
                </>
              )}
            </div>

            {/* Orange Money USSD Code */}
            {ussdCode && paymentMethod === 'ORANGE_MONEY' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm font-semibold mb-2">Code USSD à composer:</p>
                <p className="text-2xl font-mono font-bold text-center text-orange-600">
                  {ussdCode}
                </p>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Composez ce code sur votre téléphone Orange Money pour confirmer
                </p>
              </div>
            )}

            {/* Manual Payment Waiting */}
            {(paymentMethod === 'CASH' || paymentMethod === 'CHEQUE') && polling && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <p className="text-sm font-semibold text-yellow-800">En attente de validation</p>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Le Super Admin vérifiera votre paiement et validera votre accès dans les plus brefs délais.
                </p>
              </div>
            )}

            {/* Transaction/Payment ID */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>{paymentMethod === 'ORANGE_MONEY' ? 'Transaction ID' : 'Paiement ID'}:</strong> {transactionId || paymentId}
              </p>
              {paymentMethod && (
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Méthode:</strong> {
                    paymentMethod === 'ORANGE_MONEY' ? 'Orange Money' :
                    paymentMethod === 'CASH' ? 'Cash' : 'Chèque'
                  }
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BaseModulesSubscriptionDialog;
